import React, { useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { User, Calendar, Activity, FileText } from 'lucide-react';
import { LeverageRecord } from '../../Interfaces/mt5Interface';
import { getStatusColor, capitalizeFirst } from '../../utils';

export interface ViewLeverageDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  record: LeverageRecord | null;
}

const formatLeverageVal = (val: string | number | undefined | null): string => {
  if (val === undefined || val === null || String(val).trim() === '')
    return '-';
  const str = String(val).trim();
  return str.startsWith('1:') ? str.substring(2) : str;
};

export const ViewLeverageDetailsModal: React.FC<
  ViewLeverageDetailsModalProps
> = ({ isOpen, onClose, record }) => {
  useEffect(() => {
    return () => {
      document.body.style.pointerEvents = '';
    };
  }, [isOpen]);

  if (!record) return null;

  const mt5Login = record.mt5Login || record.accountNumber || '';
  const status = (record.status || 'approved').toLowerCase();

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className="sm:max-w-lg w-[95vw] max-h-[90vh] flex flex-col p-0 overflow-hidden rounded-xl shadow-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800">
        {/* Sticky Header */}
        <DialogHeader className="p-2 border-b border-gray-100 dark:border-gray-800 shrink-0 space-y-1">
          <div className="flex items-center gap-3">
            <div>
              <DialogTitle className="text-xl font-bold text-gray-900 dark:text-white">
                Leverage Request Details
              </DialogTitle>
            </div>
          </div>
        </DialogHeader>

        {/* Scrollable Body Content */}
        <div className="flex-1 min-h-0 overflow-y-auto p-2 space-y-2">
          <div className="p-2 rounded-xl bg-gradient-to-r from-gray-50 to-blue-50/30 dark:from-gray-800/40 dark:to-blue-950/20 border border-gray-200/80 dark:border-gray-800 flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                MT5 Login ID
              </span>
              <p className="font-mono text-xl font-extrabold text-gray-900 dark:text-white">
                {mt5Login || '-'}
              </p>
            </div>
            <div className="text-right">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">
                Status
              </span>
              <Badge className={getStatusColor(status, 'status')}>
                {capitalizeFirst(status)}
              </Badge>
            </div>
          </div>

          {/* Detailed Info Grid */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                <User className="h-3.5 w-3.5" />
                <span>Account Holder</span>
              </div>
              <p className="font-semibold text-gray-900 dark:text-white truncate">
                {typeof record.requestedBy === 'object'
                  ? (record.requestedBy as any)?.fullName ||
                    (record.requestedBy as any)?.email
                  : record.requestedBy || '-'}
              </p>
            </div>

            <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                <Activity className="h-3.5 w-3.5" />
                <span>Account Group</span>
              </div>
              <p className="font-semibold text-gray-900 dark:text-white truncate">
                {record.group || record.accountType || '-'}
              </p>
            </div>

            <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800">
              <div className="text-xs text-gray-500 mb-1">Old Leverage</div>
              <p className="font-bold text-gray-700 dark:text-gray-300">
                {formatLeverageVal(
                  record.oldLeverage ?? record.previousLeverage
                )}
              </p>
            </div>

            <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800">
              <div className="text-xs text-gray-500 mb-1">New Leverage</div>
              <p className="font-bold text-blue-600 dark:text-blue-400">
                {formatLeverageVal(record.newLeverage)}
              </p>
            </div>

            <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                <Calendar className="h-3.5 w-3.5" />
                <span>Date & Time</span>
              </div>
              <p className="font-medium text-gray-800 dark:text-gray-200 text-xs">
                {record.changeDate || '-'} {record.changeTime || ''}
              </p>
            </div>
          </div>

          {/* Reason Section */}
          <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
              <FileText className="h-3.5 w-3.5" />
              <span>Reason</span>
            </div>
            <p className="text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-mono bg-white dark:bg-gray-900 p-2.5 rounded border border-gray-200 dark:border-gray-700">
              {record.reason || 'No reason provided'}
            </p>
          </div>
        </div>

        {/* Sticky Footer */}
        <DialogFooter className="p-2 border-t border-gray-100 dark:border-gray-800 shrink-0 bg-white dark:bg-gray-900 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ViewLeverageDetailsModal;
